import React, { useState, useEffect, useMemo } from "react"
import {
  Table,
  Input,
  Tag,
  Avatar,
  Select,
  Pagination,
  Spin,
  message,
} from "antd"
import {
  UnorderedListOutlined,
  AppstoreOutlined,
  SearchOutlined,
} from "@ant-design/icons"
import { translations } from "../../utils/Translation/staticTranslation"
import { Analytics } from "../../Apis/analyticsApi"
import { Discounts as DiscountsApi } from "../../Apis/discountsApi"
import "./Discounts.css"

const { Option } = Select

// Helper function to get country name from code
const getCountryName = (code, lang = "en") => {
  if (!code) return "—"
  try {
    const regionNames = new Intl.DisplayNames([lang === "ar" ? "ar" : "en"], {
      type: "region",
    })
    return regionNames.of(code.toUpperCase())
  } catch {
    return code.toUpperCase()
  }
}

const DiscountAudienceStep = ({
  formData,
  setFormData,
  viewMode = false,
  discountId = null,
}) => {
  const lang = localStorage.getItem("language") || "ar"
  const t =
    translations[lang]?.discountsPage || translations[lang]?.campaignsPage
  const [viewType, setViewType] = useState("types") // "list" or "types"
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [searchText, setSearchText] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [typeFilter, setTypeFilter] = useState(null)
  const [countryFilter, setCountryFilter] = useState([])
  const [statusFilter, setStatusFilter] = useState(null)
  const [userData, setUserData] = useState([])
  const [loading, setLoading] = useState(false)
  const [availableCountries, setAvailableCountries] = useState([])
  const [countriesList, setCountriesList] = useState([])
  const [countriesLoading, setCountriesLoading] = useState(false)
  const [audienceCounts, setAudienceCounts] = useState({
    all_users: 0,
    active_users: 0,
    inactive_users: 0,
    paid_users: 0,
    unpaid_users: 0,
    newsletter_users: 0,
    returning_users: 0,
  })
  const [loadingCounts, setLoadingCounts] = useState(false)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  })

  // Helper function to normalize ID for comparison
  const normalizeId = (id) => {
    const numId = Number(id)
    return isNaN(numId) ? id : numId
  }

  // Helper function to check if ID exists in array (handles type mismatches)
  const idExists = (id, array) => {
    const normalized = normalizeId(id)
    return array.some((item) => normalizeId(item) === normalized)
  }

  const handleChange = (value) => {
    if (viewMode) return
    setFormData((prev) => ({
      ...prev,
      audience: value,
    }))
  }

  // Format number with commas
  const formatNumber = (num) => {
    return num?.toLocaleString() || "0"
  }

  const audienceOptions = [
    {
      label: t?.audience?.all_users || "All Users",
      value: "All Users",
      countKey: "all_users",
    },
    {
      label: t?.audience?.returning_users || "Returning Users",
      value: "Returning Users",
      countKey: "returning_users",
    },
    {
      label: t?.audience?.active_users || "Active Users",
      value: "Active Users",
      countKey: "active_users",
    },
    {
      label: t?.audience?.inactive_users || "Inactive Users",
      value: "Inactive Users",
      countKey: "inactive_users",
    },
    {
      label: t?.audience?.paid_users || "Paid Users",
      value: "Paid Users",
      countKey: "paid_users",
    },
    {
      label: t?.audience?.unpaid_users || "Unpaid Users",
      value: "Unpaid Users",
      countKey: "unpaid_users",
    },
    {
      label: t?.audience?.newsletter_users || "Newsletter Users",
      value: "Newsletter Users",
      countKey: "newsletter_users",
    },
  ]

  // Fetch user counts - always use discounts API
  const fetchUsersCount = async () => {
    // Get selected countries from formData
    const selectedCountries = formData?.targetCountries || []
    const countriesToUse =
      Array.isArray(selectedCountries) && selectedCountries.length > 0
        ? selectedCountries
        : []

    setLoadingCounts(true)
    try {
      // Call API with countries if selected, or empty array if no countries selected
      // Empty array will result in API call without country parameter
      const response = await DiscountsApi.getUsersCount(countriesToUse)

      if (response.status === 200 || response?.data?.success) {
        const result = response.data?.result || response.data
        if (result) {
          setAudienceCounts({
            all_users: result.all_users || 0,
            active_users: result.active_users || 0,
            inactive_users: result.inactive_users || 0,
            paid_users: result.paid_users || 0,
            unpaid_users: result.unpaid_users || 0,
            newsletter_users: result.newsletter_users || 0,
            returning_users: result.returning_users || 0,
          })
        }
      }
    } catch (error) {
      console.error("Error fetching user counts:", error)
    } finally {
      setLoadingCounts(false)
    }
  }

  // Fetch countries list from API
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setCountriesLoading(true)
        const response = await DiscountsApi.getCountryFilter()
        const countryCodes = response?.data?.result?.available_countries || []

        const formatter = new Intl.DisplayNames([lang === "ar" ? "ar" : "en"], {
          type: "region",
        })

        const formattedCountries = countryCodes
          .map((code) => {
            const name = formatter.of(code) || code
            return { code, name }
          })
          .sort((a, b) =>
            a.name.localeCompare(b.name, lang === "ar" ? "ar" : "en")
          )

        setCountriesList(formattedCountries)
      } catch (error) {
        console.error("Error fetching countries:", error)
        setCountriesList([])
      } finally {
        setCountriesLoading(false)
      }
    }

    fetchCountries()
  }, [lang])

  // Fetch counts on component mount and when countries change
  useEffect(() => {
    fetchUsersCount()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(formData?.targetCountries)])

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText), 600)
    return () => clearTimeout(timer)
  }, [searchText])

  // Fetch users from API
  const fetchUsers = async (page = 1) => {
    setLoading(true)
    try {
      // Map status filter
      let apiStatus = ""
      if (statusFilter === "Active") {
        apiStatus = "active"
      } else if (statusFilter === "Inactive") {
        apiStatus = "inactive"
      }

      // Map plan filter
      let apiPlan = ""
      if (typeFilter) {
        // Map "Free Trial" to "free" for API compatibility
        if (typeFilter === "Free Trial") {
          apiPlan = "free"
        } else {
          apiPlan = typeFilter.toLowerCase()
        }
      }

      // Get countries: use countryFilter if set, otherwise use all countries from formData.targetCountries
      const selectedCountries = formData?.targetCountries || []
      const countriesToUse =
        countryFilter && countryFilter.length > 0
          ? countryFilter
          : Array.isArray(selectedCountries) && selectedCountries.length > 0
          ? selectedCountries
          : []

      // Join multiple countries with "+" for API
      const countryParam =
        countriesToUse.length > 0 ? countriesToUse.join("+") : null

      // Use DiscountsApi.getUserList with all filters including pagination
      const res = await DiscountsApi.getUserList(
        discountId,
        countryParam,
        apiStatus || null,
        debouncedSearch || null,
        apiPlan || null,
        page,
        pagination.pageSize
      )

      const responseData = res?.data || res
      const results =
        responseData?.results ||
        responseData?.data?.results ||
        responseData ||
        []

      // Update pagination from response
      const totalCount =
        responseData?.count || responseData?.total || results.length

      setPagination({
        current: page,
        pageSize: 10,
        total: totalCount,
      })

      // Format user data - handle different response formats
      const formatted = results.map((user) => {
        const code = user.country ? user.country.toUpperCase() : null
        // Handle both user_id and id
        const userId =
          Number(user.user_id || user.id) || user.user_id || user.id
        return {
          key: userId,
          id: userId,
          name: user.name || user.user_name || "—",
          email: user.email || "—",
          country: getCountryName(code, lang),
          countryCode: code,
          plan:
            lang === "ar"
              ? user.plan_arabic || user.plan
              : user.active_plan || user.plan || "None",
          status:
            user.is_active !== undefined
              ? user.is_active
                ? "Active"
                : "Inactive"
              : user.status || "Active",
          lastActivity:
            lang === "ar"
              ? user.last_activity_arabic || user.last_activity || "—"
              : user.last_activity || "—",
        }
      })

      setUserData(formatted)
    } catch (err) {
      console.error("Error fetching users:", err)
      message.error(
        t?.audience?.failed_to_fetch_users ||
          "Failed to fetch users. Please try again."
      )
      setUserData([])
    } finally {
      setLoading(false)
    }
  }

  // Initialize selectedRowKeys from formData.selectedUsers when editing
  useEffect(() => {
    if (formData.selectedUsers && formData.selectedUsers.length > 0) {
      // Normalize IDs to match row keys (table uses numeric IDs)
      const keys = formData.selectedUsers.map((id) => normalizeId(id))
      setSelectedRowKeys(keys)
      // If custom users are selected, switch to list view
      if (formData.audience === "Custom Users" || keys.length > 0) {
        setViewType("list")
      }
    } else {
      setSelectedRowKeys([])
    }
  }, [formData.selectedUsers, formData.audience])

  // Auto-select countries from first page when switching to list view
  useEffect(() => {
    if (
      viewType === "list" &&
      formData?.targetCountries?.length > 0 &&
      countryFilter.length === 0
    ) {
      // Set all selected countries from first page
      const countries = Array.isArray(formData.targetCountries)
        ? formData.targetCountries
        : [formData.targetCountries]
      if (countries.length > 0) {
        setCountryFilter(countries)
      }
    }
  }, [viewType, formData?.targetCountries])

  // Re-fetch when filters/search change
  useEffect(() => {
    if (viewType === "list") {
      fetchUsers(1)
      setPagination((prev) => ({ ...prev, current: 1 }))
    }
  }, [debouncedSearch, typeFilter, countryFilter, statusFilter, viewType])

  const getStatusClassName = (status) => {
    switch (status) {
      case "Active":
        return "status-tag status-tag-active"
      case "Inactive":
        return "status-tag status-tag-inactive"
      default:
        return "status-tag"
    }
  }

  const getPlanClassName = (plan) => {
    // Check for "None" or empty/null plan
    if (!plan || plan === "None" || plan === "—" || plan.trim() === "") {
      return "plan-tag plan-tag-none"
    }

    // Normalize Arabic to English for consistent color mapping
    const normalizedPlan =
      plan === "برو"
        ? "Pro"
        : plan === "بيسك"
        ? "Basic"
        : plan === "بزنس"
        ? "Business"
        : plan === "ستارتر"
        ? "Starter"
        : plan === "التجربة المجانية"
        ? "Free Trial"
        : plan === "التجربة المجانية"
        ? "Free Trial"
        : plan

    switch (normalizedPlan) {
      case "Pro":
        return "plan-tag plan-tag-pro"
      case "Starter":
        return "plan-tag plan-tag-starter"
      case "Basic":
        return "plan-tag plan-tag-basic"
      case "Business":
        return "plan-tag plan-tag-business"
      case "Free":
      case "Free Trial":
        return "plan-tag plan-tag-free"
      default:
        return "plan-tag plan-tag-none"
    }
  }

  const columns = [
    {
      title: t?.audience?.table?.user || "User",
      dataIndex: "name",
      key: "user",
      render: (text, record) => {
        const avatarBgColor = getAvatarColor(text, record.id)
        const avatarTextColor = getAvatarTextColor(avatarBgColor)
        const initials = text
          ? text
              .split(" ")
              .map((n) => n[0])
              .join("")
              .slice(0, 2)
              .toUpperCase()
          : "U"

        return (
          <div className="user-cell">
            <Avatar
              src={record.avatar || null}
              className="audience-avatar"
              style={{
                backgroundColor: avatarBgColor,
                color: avatarTextColor,
              }}
            >
              {!record.avatar && initials}
            </Avatar>
            <div className="user-info">
              <div className="user-name">{text}</div>
              <div className="user-email">{record.email}</div>
            </div>
          </div>
        )
      },
    },
    {
      title: t?.audience?.table?.country || "Country",
      dataIndex: "country",
      key: "country",
      render: (text, record) => (
        <div className="country-cell">
          <span className="country-flag">
            {getCountryFlag(record.countryCode)}
          </span>
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: t?.audience?.table?.status || "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag className={getStatusClassName(status)}>
          {status === "Active"
            ? t?.audience?.status?.active || "Active"
            : t?.audience?.status?.inactive || "Inactive"}
        </Tag>
      ),
    },
    {
      title: t?.audience?.table?.last_activity || "Last Activity",
      dataIndex: "lastActivity",
      key: "lastActivity",
    },
    {
      title: t?.audience?.table?.plan || "Plan",
      dataIndex: "plan",
      key: "plan",
      render: (plan) => <Tag className={getPlanClassName(plan)}>{plan}</Tag>,
    },
  ]

  const getCountryFlag = (code) => {
    if (!code) return "🌍"
    // Convert country code to flag emoji
    const codePoints = code
      .toUpperCase()
      .split("")
      .map((char) => 127397 + char.charCodeAt())
    return String.fromCodePoint(...codePoints)
  }

  // Generate consistent background color for avatar based on user name or ID
  const getAvatarColor = (name, id) => {
    const colors = [
      "#E0F7FA", // Light cyan
      "#F3E5F5", // Light purple
      "#E8F5E9", // Light green
      "#FFF3E0", // Light orange
      "#E1F5FE", // Light blue
      "#FCE4EC", // Light pink
      "#F1F8E9", // Light lime
      "#E0F2F1", // Light teal
      "#FFF8E1", // Light yellow
      "#EDE7F6", // Light deep purple
      "#E8EAF6", // Light indigo
      "#FFEBEE", // Light red
      "#E0F7FA", // Light cyan (duplicate for more variety)
      "#F3E5F5", // Light purple (duplicate)
    ]

    // Use name or ID to generate a consistent index
    const identifier = name || id || "default"
    let hash = 0
    for (let i = 0; i < identifier.length; i++) {
      hash = identifier.charCodeAt(i) + ((hash << 5) - hash)
    }
    const index = Math.abs(hash) % colors.length
    return colors[index]
  }

  // Get text color (dark) for better contrast
  const getAvatarTextColor = (bgColor) => {
    // Return dark color for light backgrounds
    return "#111"
  }

  const rowSelection = viewMode
    ? {
        selectedRowKeys,
        onChange: () => {
          // Disabled in view mode - no changes allowed
        },
        getCheckboxProps: (record) => ({
          disabled: true, // Disable checkboxes in view mode
          name: String(record.id),
        }),
        hideSelectAll: true, // Hide select all checkbox in view mode
      }
    : {
        selectedRowKeys,
        onChange: (keys) => {
          // Get current page user IDs (normalized)
          const currentPageIds = userData.map((user) => normalizeId(user.id))

          // Normalize keys to numbers for comparison
          const normalizedKeys = keys.map((key) => normalizeId(key))

          // Find which users were selected/deselected on current page
          const newlySelected = normalizedKeys.filter(
            (key) => !idExists(key, selectedRowKeys)
          )
          const newlyDeselected = currentPageIds.filter(
            (id) => !idExists(id, normalizedKeys)
          )

          // Merge: add newly selected, remove newly deselected
          let updatedKeys = [...selectedRowKeys]

          // Add newly selected keys
          newlySelected.forEach((key) => {
            if (!idExists(key, updatedKeys)) {
              updatedKeys.push(key)
            }
          })

          // Remove newly deselected keys
          updatedKeys = updatedKeys.filter(
            (key) => !idExists(key, newlyDeselected)
          )

          setSelectedRowKeys(updatedKeys)

          // Keep as numbers for API (recipients should be numeric IDs)
          const userIds = updatedKeys.map((key) => normalizeId(key))

          setFormData((prev) => ({
            ...prev,
            selectedUsers: userIds,
            // Set audience to "Custom Users" when users are selected from list
            audience: userIds.length > 0 ? "Custom Users" : prev.audience,
          }))
        },
        getCheckboxProps: (record) => ({
          // Ensure the checkbox works with numeric IDs
          name: String(record.id),
        }),
      }

  // Sort user data to show selected users at the top
  const filteredData = useMemo(() => {
    if (selectedRowKeys.length === 0) return userData

    return [...userData].sort((a, b) => {
      const aIsSelected = idExists(a.id, selectedRowKeys)
      const bIsSelected = idExists(b.id, selectedRowKeys)

      if (aIsSelected && !bIsSelected) return -1 // a comes first
      if (!aIsSelected && bIsSelected) return 1 // b comes first
      return 0 // keep original order for both selected or both unselected
    })
  }, [userData, selectedRowKeys])

  return (
    <div className="audience-step-container">
      <div className="audience-step-header">
        <h3 className="audience-step-title">
          {t?.audience?.select_target_audience || "Select Target Audience"}
        </h3>
        <div className="audience-view-toggles">
          <button
            className={`view-toggle-btn ${viewType === "list" ? "active" : ""}`}
            onClick={() => !viewMode && setViewType("list")}
            disabled={viewMode}
          >
            <UnorderedListOutlined />
            <span>{t?.audience?.list || "List"}</span>
          </button>
          <button
            className={`view-toggle-btn ${
              viewType === "types" ? "active" : ""
            }`}
            onClick={() => !viewMode && setViewType("types")}
            disabled={viewMode}
          >
            <AppstoreOutlined />
            <span>{t?.audience?.types || "Types"}</span>
          </button>
        </div>
      </div>

      {viewType === "list" ? (
        <div className="audience-list-view">
          <div className="audience-table-container audience-table-wrapper">
            <div className="audience-filters-section audience-filters-section-wrapper">
              <div className="audience-filters-row">
                <Input
                  placeholder={t?.audience?.search || "Search"}
                  allowClear
                  prefix={<SearchOutlined className="audience-search-icon" />}
                  value={searchText}
                  onChange={(e) => !viewMode && setSearchText(e.target.value)}
                  className="audience-search-input"
                  disabled={viewMode}
                />
                <div className="audience-filters">
                  <Select
                    value={typeFilter}
                    onChange={setTypeFilter}
                    placeholder={t?.audience?.type || "Type"}
                    className="audience-filter-select audience-filter-select-inline"
                    allowClear
                    disabled={viewMode}
                  >
                    <Option value="Pro">{lang === "ar" ? "برو" : "Pro"}</Option>
                    <Option value="Starter">
                      {lang === "ar" ? "ستارتر" : "Starter"}
                    </Option>
                    <Option value="Basic">
                      {lang === "ar" ? "بيسك" : "Basic"}
                    </Option>
                    <Option value="Free Trial">
                      {lang === "ar" ? "التجربة المجانية" : "Free Trial"}
                    </Option>
                    <Option value="Business">
                      {lang === "ar" ? "أعمال" : "Business"}
                    </Option>
                  </Select>
                  <Select
                    mode="multiple"
                    value={countryFilter}
                    onChange={setCountryFilter}
                    placeholder={t?.audience?.country || "Country"}
                    className="audience-filter-select audience-filter-select-inline audience-filter-select-country"
                    allowClear
                    disabled={viewMode}
                    loading={countriesLoading}
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? option?.children ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    notFoundContent={
                      countriesLoading ? "Loading..." : "No countries found"
                    }
                    maxTagCount="responsive"
                  >
                    {countriesList.map((country) => (
                      <Option
                        key={country.code}
                        value={country.code}
                        label={country.name}
                      >
                        <img
                          src={`https://flagcdn.com/w20/${country.code.toLowerCase()}.png`}
                          alt={country.code}
                          style={{
                            width: "20px",
                            height: "15px",
                            marginRight: "8px",
                            verticalAlign: "middle",
                          }}
                          onError={(e) => {
                            e.target.style.display = "none"
                          }}
                        />
                        {country.name}
                      </Option>
                    ))}
                  </Select>
                  <Select
                    value={statusFilter}
                    onChange={setStatusFilter}
                    placeholder={t?.audience?.table?.status || "Status"}
                    className="audience-filter-select audience-filter-select-inline"
                    allowClear
                    disabled={viewMode}
                  >
                    <Option value="Active">
                      {t?.audience?.status?.active || "Active"}
                    </Option>
                    <Option value="Inactive">
                      {t?.audience?.status?.inactive || "Inactive"}
                    </Option>
                  </Select>
                </div>
              </div>
              <div className="audience-selection-summary">
                <span style={{ color: "#FB6514" }}>
                  {selectedRowKeys.length}&nbsp;
                </span>
                <span style={{ fontWeight: "500", color: "#141414" }}>
                  {t?.audience?.selected || "selected"}
                </span>{" "}
                {pagination.total} {t?.audience?.of || "of"} {pagination.total}{" "}
                {t?.audience?.available || "available"}
              </div>
            </div>
            <Spin spinning={loading}>
              <Table
                columns={columns}
                dataSource={filteredData}
                rowSelection={rowSelection}
                pagination={false}
                className="audience-table"
                rowKey="id"
                loading={loading}
              />
            </Spin>
          </div>
          <div className="audience-pagination-wrapper">
            <Pagination
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={pagination.total}
              showSizeChanger={false}
              showLessItems={false}
              itemRender={(page, type, originalElement) => {
                const totalPages = Math.ceil(
                  pagination.total / pagination.pageSize
                )

                if (type === "prev") {
                  const isDisabled =
                    pagination.current === 1 || totalPages === 0

                  const handleClick = (e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    if (isDisabled) return

                    // Always go to previous page (same logic for both LTR and RTL)
                    if (pagination.current > 1) {
                      const newPage = pagination.current - 1
                      setPagination((prev) => ({ ...prev, current: newPage }))
                      fetchUsers(newPage)
                    }
                  }

                  return (
                    <a
                      onClick={handleClick}
                      onMouseDown={(e) => {
                        if (isDisabled) {
                          e.preventDefault()
                          e.stopPropagation()
                        }
                      }}
                      style={{
                        color: isDisabled ? "#D0D5DD" : "#18181B",
                        fontSize: "14px",
                        fontWeight: 400,
                        textDecoration: "none",
                        border: "none",
                        background: "transparent",
                        padding: 0,
                        cursor: isDisabled ? "not-allowed" : "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        pointerEvents: isDisabled ? "none" : "auto",
                        opacity: isDisabled ? 0.5 : 1,
                        userSelect: "none",
                      }}
                    >
                      {lang === "ar" ? (
                        <>
                          {t?.audience?.previous || "Previous"}
                          <img
                            src="/Campaign/ChevronRight.svg"
                            alt="Previous"
                            style={{
                              width: "16px",
                              height: "16px",
                              transform: "scaleX(-1)",
                            }}
                          />
                        </>
                      ) : (
                        <>
                          <img
                            src="/Campaign/ChevronLeft.svg"
                            alt="Previous"
                            style={{ width: "16px", height: "16px" }}
                          />
                          {t?.audience?.previous || "Previous"}
                        </>
                      )}
                    </a>
                  )
                }
                if (type === "next") {
                  const isDisabled =
                    pagination.current >= totalPages || totalPages === 0

                  const handleClick = (e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    if (isDisabled) return

                    // Always go to next page (same logic for both LTR and RTL)
                    if (pagination.current < totalPages) {
                      const newPage = pagination.current + 1
                      setPagination((prev) => ({ ...prev, current: newPage }))
                      fetchUsers(newPage)
                    }
                  }

                  return (
                    <a
                      onClick={handleClick}
                      onMouseDown={(e) => {
                        if (isDisabled) {
                          e.preventDefault()
                          e.stopPropagation()
                        }
                      }}
                      style={{
                        color: isDisabled ? "#D0D5DD" : "#18181B",
                        fontSize: "14px",
                        fontWeight: 400,
                        textDecoration: "none",
                        border: "none",
                        background: "transparent",
                        padding: 0,
                        cursor: isDisabled ? "not-allowed" : "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        pointerEvents: isDisabled ? "none" : "auto",
                        opacity: isDisabled ? 0.5 : 1,
                        userSelect: "none",
                      }}
                    >
                      {lang === "ar" ? (
                        <>
                          <img
                            src="/Campaign/ChevronLeft.svg"
                            alt="Next"
                            style={{
                              width: "16px",
                              height: "16px",
                              transform: "scaleX(-1)",
                            }}
                          />
                          {t?.audience?.next || "Next"}
                        </>
                      ) : (
                        <>
                          {t?.audience?.next || "Next"}
                          <img
                            src="/Campaign/ChevronRight.svg"
                            alt="Next"
                            style={{ width: "16px", height: "16px" }}
                          />
                        </>
                      )}
                    </a>
                  )
                }
                if (type === "page") {
                  const isActive = page === pagination.current
                  return (
                    <a
                      onClick={(e) => {
                        e.preventDefault()
                        setPagination({ ...pagination, current: page })
                        fetchUsers(page)
                      }}
                      style={{
                        color: "#18181B",
                        backgroundColor: isActive ? "#F9FAFB" : "transparent",
                        padding: isActive ? "8px 14px" : "8px 14px",
                        borderRadius: isActive ? "8px" : "8px",
                        fontSize: "14px",
                        fontWeight: 400,
                        border: isActive
                          ? "1px solid #D0D5DD"
                          : "1px solid transparent",
                        textDecoration: "none",
                        display: "inline-block",
                        textAlign: "center",
                        cursor: "pointer",
                        minWidth: "36px",
                      }}
                    >
                      {page}
                    </a>
                  )
                }
                if (type === "jump-prev" || type === "jump-next") {
                  // Calculate target page for ellipsis click
                  let targetPage
                  if (type === "jump-prev") {
                    targetPage = Math.max(1, pagination.current - 5)
                  } else {
                    targetPage = Math.min(totalPages, pagination.current + 5)
                  }

                  return (
                    <a
                      onClick={(e) => {
                        e.preventDefault()
                        setPagination({ ...pagination, current: targetPage })
                        fetchUsers(targetPage)
                      }}
                      style={{
                        color: "#18181B",
                        fontSize: "14px",
                        border: "none",
                        background: "transparent",
                        padding: "8px 14px",
                        cursor: "pointer",
                        display: "inline-block",
                        textAlign: "center",
                        minWidth: "36px",
                      }}
                    >
                      ...
                    </a>
                  )
                }
                return originalElement
              }}
              onChange={(page, pageSize) => {
                setPagination({ ...pagination, current: page, pageSize })
                fetchUsers(page)
              }}
            />
          </div>
        </div>
      ) : (
        <div className="audience-cards-grid">
          {audienceOptions.map((option) => {
            const count = audienceCounts[option.countKey] || 0
            return (
              <div
                key={option.value}
                className={`audience-card ${
                  formData.audience === option.value ? "selected" : ""
                } ${viewMode ? "disabled" : ""}`}
                onClick={() => !viewMode && handleChange(option.value)}
                style={viewMode ? { cursor: "not-allowed", opacity: 0.6 } : {}}
              >
                <div className="audience-card-label">{option.label}</div>
                <div className="audience-card-count">
                  {loadingCounts
                    ? t?.audience?.loading || "Loading..."
                    : `${formatNumber(count)} ${t?.audience?.users || "users"}`}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default DiscountAudienceStep
